/**
 * preDeploymentApi — Real offline evaluation API (Smithy ops: ListEvals /
 * GetEval / GetEvalReport / GetEvalCases / GetEvalCase) via the shared
 * AgentPlatformClient (SigV4 through Cognito, same auth chain as SkillClient).
 *
 * Exposes the exact surface the tab was built against with the mock:
 *   - PreDeployRun with camelCase fields and createdAt as an ISO string
 *     (the generated client returns Date; converted here at the boundary).
 *   - listEvals paginates with nextToken.
 *   - getEvalReport throws ReportNotFoundError when the run predates the
 *     report-in-S3 cutover (the API's ResourceNotFoundException / 404).
 *   - getEvalCases lists per-case results (metrics + input/output; trace is
 *     null in the list to stay under the Lambda 6MB cap).
 *   - getEvalCase fetches ONE case WITH its trace (size-guarded server-side;
 *     traceTooLarge signals the UI to fall back to the HTML report).
 */

import {
  GetEvalCommand,
  GetEvalReportCommand,
  GetEvalCasesCommand,
  GetEvalCaseCommand,
  ListEvalsCommand,
  ResourceNotFoundException,
} from "@amzn/cmhk-agent-platform-service-client";
import type { EvalRun, EvalCase } from "@amzn/cmhk-agent-platform-service-client";
import { getSkillClient } from "../../../../client/SkillClient";

// INTERRUPTED: a deploy cut the run short before it finished — NOT a gate
// failure. The EvaluationManager reverts the batch to PENDING and re-runs it
// in the next aggregation window, so the change is still being processed.
export type EvalRunStatus = "RUNNING" | "SUCCESS" | "FAILED" | "INTERRUPTED";
export type CapabilityCategory = "skill" | "domain";
export type PromotionMode = "scope" | "change_set";
export type DeliveryMode = "email" | "pull";
export type EvalScope = "all" | "agent" | "single";

/** One changed skill/domain the run is about (change-set runs). */
export interface ChangedItem {
  category?: CapabilityCategory;
  name?: string;
}

/** Per-target outcome status, written by the Evaluation Manager after promote. */
export type PromotionStatus = "promoted" | "skipped" | "failed";

/**
 * What happened to ONE target when the run promoted. A run promotes N targets
 * (change-set / scope-batch) or one (single scope); each gets an entry so the
 * UI can show which shipped, which was skipped, and WHY — not just the
 * run-level gate verdict.
 */
export interface PromotionResult {
  category?: CapabilityCategory;
  agentName?: string;
  name?: string;
  status: PromotionStatus;
  /** Coarse machine reason (STALE / INVALID_FRONTMATTER / ...); skipped/failed. */
  reason?: string;
  /** Verbose human detail (itemized frontmatter violations, exception text). */
  error?: string;
  /** Published hub version (e.g. "v3"); present on promoted targets. */
  version?: string;
}

/**
 * UI-facing run shape. Kept local (rather than leaking the generated EvalRun,
 * whose required fields are typed `| undefined`) so the tab compiles against
 * a stable contract across model-package regenerations.
 */
export interface PreDeployRun {
  runId: string;
  status: EvalRunStatus;
  category?: CapabilityCategory;
  promotionMode?: PromotionMode;
  isComplete?: boolean;
  isPassed?: boolean;
  passRate?: number;
  /** Test cases evaluated; 0 = change matched none → "Skipped" (promoted
   *  without a gate), distinct from a blocked run that failed the gate. */
  totalTestCases?: number;
  createdAt?: string;
  s3Location?: string;
  threshold?: number;
  notify?: boolean;
  promote?: boolean;
  promoteToNextStage?: boolean;
  delivery?: DeliveryMode;
  // ── Target fields — not in the API yet (backend change pending); the
  // detail view renders them only when present, so this ships safely ahead.
  scope?: EvalScope;
  targetName?: string;
  agentName?: string;
  triggerSource?: string;
  /** "integration_test" for integration-test gate runs, absent otherwise. */
  runType?: string;
  changes?: ChangedItem[];
  /** Per-target promotion outcome, written after the promote step. Absent on
   *  runs that never promoted (still running / gate-blocked). */
  promotionResults?: PromotionResult[];
}

/** Thrown when a run has no report.html in S3 (early runs, pre-cutover). */
export class ReportNotFoundError extends Error {
  constructor(runId: string) {
    super(`No HTML report stored for run ${runId}`);
    this.name = "ReportNotFoundError";
  }
}

// Runs before this date predate report storage in S3. ListEvals already
// filters to post-cutover runs server-side, but GetEval serves any run_id
// (deep links), so the list column still needs this heuristic.
const REPORT_CUTOVER = "2026-06-20";

/** True when the run's report.html exists in S3 (post-cutover runs only).
 *  INTERRUPTED runs are excluded like RUNNING: the deploy cut them short
 *  before the worker's S3 persist step, so no report was ever written. */
export function hasStoredReport(run: PreDeployRun): boolean {
  return (
    (run.createdAt ?? "") >= REPORT_CUTOVER &&
    run.status !== "RUNNING" &&
    run.status !== "INTERRUPTED"
  );
}

/**
 * Runs newest-first by real time. ListEvals pages in the GSI's RAW `created_at`
 * string order, and the EvaluationManager stamps "+08:00" while integration /
 * manual runs stamp UTC — so the API order interleaves them ~8h off (a 11:00+08
 * change-set run, i.e. 03:00Z, sorts above a 08:32Z integration run). The tab
 * holds every page anyway, so it re-sorts here; `createdAt` is already a
 * normalized UTC ISO string (toPreDeployRun), so string order IS time order.
 * Runs without a createdAt sink to the end; ties keep API order (stable sort).
 */
export function sortNewestFirst(runs: PreDeployRun[]): PreDeployRun[] {
  return [...runs].sort((a, b) => {
    if (!a.createdAt || !b.createdAt) return (a.createdAt ? 0 : 1) - (b.createdAt ? 0 : 1);
    return a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
  });
}

function toPreDeployRun(run: EvalRun): PreDeployRun {
  // Target fields ship in a later model version than the checked-in client
  // types; read them dynamically so this code lights up when the backend does.
  const extra = run as unknown as Record<string, unknown>;
  return {
    runId: run.runId ?? "",
    status: (run.status ?? "RUNNING") as EvalRunStatus,
    category: run.category as CapabilityCategory | undefined,
    promotionMode: run.promotionMode as PromotionMode | undefined,
    isComplete: run.isComplete,
    isPassed: run.isPassed,
    passRate: run.passRate,
    // Reads dynamically (via extra) until the checked-in EvalRun type carries
    // totalTestCases — lights up as soon as the regenerated model ships.
    totalTestCases:
      typeof extra.totalTestCases === "number" ? (extra.totalTestCases as number) : undefined,
    createdAt: run.createdAt?.toISOString(),
    s3Location: run.s3Location,
    threshold: run.threshold,
    notify: run.notify,
    promote: run.promote,
    promoteToNextStage: run.promoteToNextStage,
    delivery: run.delivery as DeliveryMode | undefined,
    scope: extra.scope as EvalScope | undefined,
    targetName: (extra.targetName as string | undefined) || undefined,
    agentName: (extra.agentName as string | undefined) || undefined,
    triggerSource: (extra.triggerSource as string | undefined) || undefined,
    runType: (extra.runType as string | undefined) || undefined,
    changes: extra.changes as ChangedItem[] | undefined,
    // Read dynamically (via extra) until the checked-in EvalRun client type
    // carries promotionResults — lights up as soon as the regenerated model ships.
    promotionResults: Array.isArray(extra.promotionResults)
      ? (extra.promotionResults as PromotionResult[])
      : undefined,
  };
}

export async function listEvals(
  maxResults = 10,
  nextToken?: string,
  runType?: string
): Promise<{ items: PreDeployRun[]; nextToken?: string }> {
  // runType ("integration_test" | "normal") filters server-side. The field
  // isn't in the checked-in generated client yet, so pass it through as a
  // dynamic property — lights up as soon as the regenerated model ships (same
  // pattern as the dynamically-read response fields in toPreDeployRun).
  const input = { maxResults, nextToken } as Record<string, unknown>;
  if (runType) input.runType = runType;
  const response = await getSkillClient().send(new ListEvalsCommand(input as never));
  return {
    items: (response.items ?? []).map(toPreDeployRun),
    nextToken: response.nextToken,
  };
}

export async function getEval(runId: string): Promise<PreDeployRun> {
  const response = await getSkillClient().send(new GetEvalCommand({ runId }));
  // Single-resource output is flattened: the response IS the EvalRun.
  return toPreDeployRun(response);
}

export async function getEvalReport(
  runId: string
): Promise<{ reportUrl: string; expiresIn: number }> {
  try {
    const response = await getSkillClient().send(
      new GetEvalReportCommand({ runId })
    );
    return { reportUrl: response.reportUrl ?? "", expiresIn: response.expiresIn ?? 900 };
  } catch (e) {
    if (e instanceof ResourceNotFoundException) throw new ReportNotFoundError(runId);
    throw e;
  }
}

// ─── Per-case results (interactive case explorer) ────────────────────────────

/** One deepeval metric result (one entry of the case's `metrics` list). */
export interface CaseMetric {
  name: string;
  score: number;
  threshold: number;
  success: boolean;
  reason: string;
}

/**
 * UI-facing per-test-case shape. `passed` mirrors the run-level gate (the eval
 * manager's `_task_passed`): a case passes only when it ran to `completed` AND
 * has at least one metric AND every metric cleared its threshold. The worker's
 * `status=completed` alone just means "ran without crashing" — it says nothing
 * about scores, so deriving `passed` from status alone over-counts passes (a
 * case that ran but scored below threshold would show as passed, inflating the
 * count above the run's real pass rate). `metrics` is the API's explicit metric
 * list; `trace`/`traceTooLarge` are only populated by getEvalCase.
 */
export interface PreDeployCase {
  testCaseId: string;
  status: string;
  passed: boolean;
  input?: string;
  actualOutput?: string;
  metrics: CaseMetric[];
  /** Raw trace JSON (tool calls + metadata); only set by getEvalCase. */
  trace?: unknown;
  /** Server omitted an oversized trace — UI falls back to the HTML report. */
  traceTooLarge?: boolean;
}

/** Normalize the API's `metrics` list ([{name, score, threshold, success,
 *  reason}, ...]) into the UI shape. The API now returns an explicit array
 *  (each metric a modeled structure) rather than an opaque object, because a
 *  Document field didn't serialize server-side. Tolerant of missing/odd entries
 *  — a bad one is dropped rather than throwing, so it can't blank the panel. */
function parseMetrics(raw: unknown): CaseMetric[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((m) => m && typeof m === "object")
    .map((m: any) => ({
      name: String(m.name ?? ""),
      score: Number(m.score ?? 0),
      threshold: Number(m.threshold ?? 0),
      success: Boolean(m.success),
      reason: String(m.reason ?? ""),
    }));
}

/** The trace arrives as a raw JSON string (a String field server-side, for the
 *  same serialization reason as metrics). Parse it to an object for rendering;
 *  leave undefined when absent, or keep the raw string if it isn't valid JSON. */
function parseTrace(raw: unknown): unknown {
  if (raw == null) return undefined;
  if (typeof raw !== "string") return raw; // already structured (defensive)
  const s = raw.trim();
  if (!s) return undefined;
  try {
    return JSON.parse(s);
  } catch {
    return raw;
  }
}

/** The gate: matches the eval manager's `_task_passed` — ran to completion, has
 *  metrics, and every metric cleared its threshold. Empty/absent metrics never
 *  pass (unknown must not read as passing). */
function isCasePassed(status: string, metrics: CaseMetric[]): boolean {
  return status === "completed" && metrics.length > 0 && metrics.every((m) => m.success);
}

function toPreDeployCase(c: EvalCase): PreDeployCase {
  const status = c.status ?? "";
  // Read metrics/trace via `unknown`: the checked-in client types lag the model
  // regeneration (metrics is now MetricResult[], trace is string), so cast at
  // the boundary and let the normalizers validate the runtime shape.
  const raw = c as unknown as { metrics?: unknown; trace?: unknown };
  const metrics = parseMetrics(raw.metrics);
  return {
    testCaseId: c.testCaseId ?? "",
    status,
    passed: isCasePassed(status, metrics),
    input: c.input,
    actualOutput: c.actualOutput,
    metrics,
    trace: parseTrace(raw.trace),
    traceTooLarge: c.traceTooLarge,
  };
}

/** One page of per-case results for a run. Trace is null on every item here
 *  (the bulk list omits it for payload size) — use getEvalCase for the trace. */
export async function getEvalCases(
  runId: string,
  maxResults = 100,
  nextToken?: string
): Promise<{ items: PreDeployCase[]; nextToken?: string }> {
  const response = await getSkillClient().send(
    new GetEvalCasesCommand({ runId, maxResults, nextToken })
  );
  return {
    items: (response.items ?? []).map(toPreDeployCase),
    nextToken: response.nextToken,
  };
}

/** One case WITH its execution trace (lazily fetched when a case is opened).
 *  `traceTooLarge` true ⇒ the server omitted an oversized trace; render the
 *  HTML-report fallback instead of the trace panel. */
export async function getEvalCase(runId: string, caseId: string): Promise<PreDeployCase> {
  const response = await getSkillClient().send(
    new GetEvalCaseCommand({ runId, caseId })
  );
  // Single-resource output nests the case under `evalCase`.
  return toPreDeployCase(response.evalCase ?? ({ testCaseId: caseId } as EvalCase));
}
